import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  newsItems as seedNews,
  filterCategories,
  keyCompetitors,
  keyCustomers,
  favouriteSearches,
  MOCK_INTEL_FEED_URL,
} from '../mockData';
import { useIntel } from '../IntelContext';

function companyInitial(name) {
  if (!name || !String(name).trim()) return '?';
  const c = String(name).trim()[0];
  return /[a-zA-Z0-9]/.test(c) ? c.toUpperCase() : '?';
}

function NewsCompanyAvatar({ company }) {
  const [failed, setFailed] = useState(false);
  const logo = company?.logo;
  const canUseLogo = typeof logo === 'string' && logo.trim().length > 0 && !failed;

  if (canUseLogo) {
    return (
      <img
        src={logo}
        alt={company?.name || 'Company logo'}
        className="company-logo-square-img"
        onError={() => setFailed(true)}
      />
    );
  }

  return <>{companyInitial(company?.name)}</>;
}

const EXTRA_READ_MORE =
  ' Further procurement timelines are aligning with phased reactor procurements slated through FY2028. Procurement counsel expects intermittent tender freezes during monsoon outages at western India reactor clusters. Commentary from fabrication partners reinforces that lining retrofits coincide with discretionary capex board approvals previously logged in Vizag corridors.';

const WORKSPACES = ['Strategy', 'Marketing', 'Sales'];

function seedTabFilter(tab, customs, cards) {
  if (customs.some((ct) => ct.id === tab)) return cards;
  if (tab === 'key') return cards.filter((c) => [1, 3].includes(c.id));
  if (tab === 'competitors') return cards.filter((c) => c.id === 2);
  if (tab === 'areas') return cards.filter((c) => [4, 5].includes(c.id));
  if (tab === 'tenders')
    return cards.filter((c) => normalizeSourceFacet(c.source) === 'Government Tender');
  if (tab === 'leadership') return [];
  return cards;
}

function normalizeSourceFacet(source) {
  const s = String(source);
  if (s === 'Gazette Notification') return 'Govt Notification';
  if (s === 'Business Standard') return 'News Article';
  return s;
}

const competitorNames = new Set(keyCompetitors.map((c) => c.name));
const customerNames = new Set(keyCustomers.map((c) => c.name));

function ribbonClass(sidebarCompaniesActiveIds, categories, cardCompany) {
  const ordered = [...categories.companies];
  const activeCats = ordered.filter((c) => sidebarCompaniesActiveIds.includes(c.id));
  for (const cat of activeCats) {
    if (cat.name === 'Key Competitors' && competitorNames.has(cardCompany))
      return 'ribbon-competitors';
    if (cat.name === 'Customers' && customerNames.has(cardCompany)) return 'ribbon-customers';
    if (cat.name === 'Suppliers' && /\bsupplier/i.test(cardCompany)) return 'ribbon-suppliers';
    if (cat.name === 'Partners' && /\bpartner\b/i.test(cardCompany)) return 'ribbon-partners';
  }
  return '';
}

function parseNewsDate(ds) {
  const m = /^(\d{2}) (\w{3}) (\d{4})$/.exec(ds);
  if (!m) return null;
  return Date.parse(`${m[2]} ${m[1]}, ${m[3]}`);
}

function endOfDay(ms) {
  const d = new Date(ms);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function barsFromCards(cards) {
  const take = cards.slice(0, 5);
  const strengths = [88, 72, 94, 81, 76];
  return take.map((c, i) => ({
    label: c.company.name.split(/\s+/)[0],
    strength: strengths[i % strengths.length],
  }));
}

function NewsVizBars({ bars }) {
  const max = Math.max(...bars.map((b) => b.strength), 1);
  const w = 420;
  const h = 180;
  const pad = { l: 40, r: 12, b: 48, t: 16 };
  const bw = bars.length ? (w - pad.l - pad.r) / bars.length - 8 : 0;
  return (
    <div className="nf-viz-wrap" aria-hidden>
      <svg
        width="100%"
        viewBox={`0 0 ${w} ${h}`}
        className="nf-viz-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <line
            key={t}
            x1={pad.l}
            x2={w - pad.r}
            y1={pad.t + (1 - t) * (h - pad.t - pad.b)}
            y2={pad.t + (1 - t) * (h - pad.t - pad.b)}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
        ))}
        {bars.map((b, i) => {
          const barH =
            ((h - pad.t - pad.b) * b.strength) / max;
          const x = pad.l + i * (bw + 8);
          const y = h - pad.b - barH;
          return (
            <g key={b.label}>
              <rect x={x} y={y} width={bw} height={barH} rx={4} fill="#2563eb" opacity="0.85" />
              <text x={x + bw / 2} y={h - 12} textAnchor="middle" fontSize="9" fill="#475569">
                {b.label}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="nf-viz-caption">Mock signal strength (relative) by focal company.</p>
    </div>
  );
}

function NewsfeedPage({ intelBridge }) {
  const { toast } = useIntel();
  const bridgeNonce = intelBridge?.nonce;

  const [items, setItems] = useState(() =>
    seedNews.map((x) => ({
      ...x,
      bookmarked: false,
      reviewed: false,
      editedTitle: null,
      editedDesc: null,
      expanded: false,
      editing: false,
      hidden: false,
    })),
  );

  const [activeTab, setActiveTab] = useState('all');
  const [customTabs, setCustomTabs] = useState([]);
  const [tabAddOpen, setTabAddOpen] = useState(false);
  const [newTabLabel, setNewTabLabel] = useState('');

  const [search, setSearch] = useState('');
  const lastBridgeRef = useRef(-1);

  useEffect(() => {
    if (typeof bridgeNonce !== 'number' || bridgeNonce === lastBridgeRef.current) return;
    lastBridgeRef.current = bridgeNonce;
    if (intelBridge?.newsfeedSearch != null && intelBridge.newsfeedSearch !== undefined) {
      setSearch(String(intelBridge.newsfeedSearch));
    }
    if (intelBridge?.newsfeedSubTab) setActiveTab(intelBridge.newsfeedSubTab);
    if (intelBridge?.newsfeedWordCloudTerm != null) {
      setSearch(String(intelBridge.newsfeedWordCloudTerm));
    }
  }, [bridgeNonce, intelBridge]);

  const [viewMode, setViewMode] = useState('list');
  const [timeLabel, setTimeLabel] = useState('Last 7 days');
  const [timeOpen, setTimeOpen] = useState(false);

  const [companiesSel, setCompaniesSel] = useState([]);
  const [topicsSel, setTopicsSel] = useState([]);
  const [sourcesSel, setSourcesSel] = useState([]);
  const [locsSel, setLocsSel] = useState([]);

  const [sortKey, setSortKey] = useState('Date Published');
  const [dupLabel, setDupLabel] = useState('Grouped');
  const [commentLabel, setCommentLabel] = useState('All');
  const [curationMode, setCurationMode] = useState('On');

  const [advOpen, setAdvOpen] = useState(false);
  const [advFrom, setAdvFrom] = useState('');
  const [advTo, setAdvTo] = useState('');
  const [advCos, setAdvCos] = useState(() =>
    Object.fromEntries([...new Set(seedNews.map((n) => n.company.name))].map((n) => [n, true])),
  );
  const [advSources, setAdvSources] = useState(() =>
    Object.fromEntries(filterCategories.sourcesContent.map((s) => [s.name, true])),
  );

  const [favDdOpen, setFavDdOpen] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [wsModalCardId, setWsModalCardId] = useState(null);
  const [wsPick, setWsPick] = useState(WORKSPACES[0]);
  const [moreCardId, setMoreCardId] = useState(null);

  const [fdHeadline, setFdHeadline] = useState('');
  const [fdCompany, setFdCompany] = useState(seedNews[0].company.name);
  const [fdSource, setFdSource] = useState('News Article');
  const [fdDate, setFdDate] = useState('03 May 2026');
  const [fdSummary, setFdSummary] = useState('');

  function toggleSidebar(id, arr, setFn) {
    setFn((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const filteredBase = useMemo(() => {
    let list = items.filter((item) => !item.hidden);

    list = seedTabFilter(activeTab, customTabs, list);

    const qSearch = search.trim().toLowerCase();
    if (qSearch)
      list = list.filter(
        (item) =>
          item.title.toLowerCase().includes(qSearch) ||
          item.company.name.toLowerCase().includes(qSearch),
      );

    if (sourcesSel.length) {
      list = list.filter((item) => {
        const facet = normalizeSourceFacet(item.source);
        return sourcesSel.some((sid) => {
          const nm = filterCategories.sourcesContent.find((s) => s.id === sid)?.name;
          return nm && facet === nm;
        });
      });
    }

    list = list.filter((item) => {
      const tFrom = advFrom ? Date.parse(advFrom) : null;
      const tTo = advTo ? Date.parse(advTo) : null;
      if (!Number.isFinite(tFrom) && !Number.isFinite(tTo)) {
        /**/
      } else {
        const dm = parseNewsDate(item.date);
        if (dm == null) return false;
        if (Number.isFinite(tFrom) && dm < tFrom) return false;
        if (Number.isFinite(tTo) && dm > endOfDay(tTo)) return false;
      }
      const cosOn = Object.entries(advCos)
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (cosOn.length && !cosOn.includes(item.company.name)) return false;
      const srcOn = Object.entries(advSources)
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (
        srcOn.length &&
        !srcOn.includes(normalizeSourceFacet(item.source))
      ) {
        return false;
      }
      return true;
    });

    const copy = [...list];
    const companySort = (a, b) =>
      String(a.company.name).localeCompare(String(b.company.name));

    if (sortKey === 'Date Published')
      copy.sort((a, b) => (parseNewsDate(b.date) || 0) - (parseNewsDate(a.date) || 0));
    if (sortKey === 'Relevance') copy.sort((a, b) => b.id - a.id);
    if (sortKey === 'Company') copy.sort(companySort);

    return copy;
  }, [
    items,
    activeTab,
    customTabs,
    search,
    sourcesSel,
    sortKey,
    advFrom,
    advTo,
    advCos,
    advSources,
  ]);

  const vizBars = useMemo(() => barsFromCards(filteredBase), [filteredBase]);

  const addCardFromModal = () => {
    const headline = fdHeadline.trim();
    const summary = fdSummary.trim();
    if (!headline || !summary) {
      toast('Fill headline and summary');
      return;
    }
    const nid = Math.max(0, ...items.map((i) => i.id)) + 1;
    setItems((prev) => [
      {
        id: nid,
        company: { name: fdCompany, logo: '📎', color: '#334155' },
        source: fdSource,
        date: fdDate,
        publishedDate: fdDate,
        title: headline,
        description: summary,
        image: '📎',
        keyHighlights: [],
        bookmarked: false,
        reviewed: false,
        editedTitle: null,
        editedDesc: null,
        expanded: false,
        editing: false,
        hidden: false,
      },
      ...prev,
    ]);
    toast('Update added');
    setAddModal(false);
    setFdHeadline('');
    setFdSummary('');
  };

  const advResetAll = () => {
    setAdvCos(
      Object.fromEntries([...new Set(seedNews.map((n) => n.company.name))].map((n) => [n, true])),
    );
    setAdvSources(
      Object.fromEntries(filterCategories.sourcesContent.map((s) => [s.name, true])),
    );
    setAdvFrom('');
    setAdvTo('');
  };

  async function clipboardWrite(text, okMsg) {
    try {
      await navigator.clipboard.writeText(text);
      toast(okMsg);
    } catch {
      toast('Clipboard unavailable — mock link ready in toast');
      toast(text);
    }
  }

  return (
    <div className="newsfeed-page">
      {/* Workspace modal */}
      {wsModalCardId !== null ? (
        <div
          className="gmm-modal-backdrop"
          role="presentation"
          onClick={() => setWsModalCardId(null)}
        >
          <div className="gmm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="gmm-modal-title">Add to workspace</h3>
            <select className="gmm-input" value={wsPick} onChange={(e) => setWsPick(e.target.value)}>
              {WORKSPACES.map((w) => (
                <option key={w} value={w}>
                  {w}
                </option>
              ))}
            </select>
            <div className="gmm-modal-actions">
              <button type="button" className="gmm-btn-muted" onClick={() => setWsModalCardId(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="gmm-btn-primary"
                onClick={() => {
                  toast(`Added to ${wsPick}`);
                  setWsModalCardId(null);
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Add-update modal */}
      {addModal ? (
        <div
          className="gmm-modal-backdrop"
          role="presentation"
          onClick={() => setAddModal(false)}
        >
          <div className="gmm-modal gmm-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3 className="gmm-modal-title">Add Update</h3>
            <label className="gmm-field-label">
              Headline
              <input
                className="gmm-input"
                value={fdHeadline}
                onChange={(e) => setFdHeadline(e.target.value)}
              />
            </label>
            <label className="gmm-field-label">
              Company
              <select className="gmm-input" value={fdCompany} onChange={(e) => setFdCompany(e.target.value)}>
                {[...new Set(seedNews.map((n) => n.company.name))].map((nm) => (
                  <option key={nm} value={nm}>
                    {nm}
                  </option>
                ))}
              </select>
            </label>
            <label className="gmm-field-label">
              Source type
              <select className="gmm-input" value={fdSource} onChange={(e) => setFdSource(e.target.value)}>
                {filterCategories.sourcesContent.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="gmm-field-label">
              Date
              <input
                className="gmm-input"
                value={fdDate}
                onChange={(e) => setFdDate(e.target.value)}
                placeholder="DD Mon YYYY"
              />
            </label>
            <label className="gmm-field-label">
              Summary
              <textarea className="gmm-textarea" value={fdSummary} onChange={(e) => setFdSummary(e.target.value)} />
            </label>
            <div className="gmm-modal-actions">
              <button type="button" className="gmm-btn-muted" onClick={() => setAddModal(false)}>
                Cancel
              </button>
              <button type="button" className="gmm-btn-primary" onClick={addCardFromModal}>
                Add
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {advOpen ? (
        <div className="gmm-modal-backdrop" role="presentation" onClick={() => setAdvOpen(false)}>
          <div className="gmm-modal gmm-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3 className="gmm-modal-title">Advanced Search</h3>
            <div className="gmm-two-col">
              <label className="gmm-field-label">
                From
                <input type="date" className="gmm-input" value={advFrom} onChange={(e) => setAdvFrom(e.target.value)} />
              </label>
              <label className="gmm-field-label">
                To
                <input type="date" className="gmm-input" value={advTo} onChange={(e) => setAdvTo(e.target.value)} />
              </label>
            </div>
            <div className="gmm-checkbox-grid">
              <span className="gmm-strong">Companies</span>
              {[...new Set(seedNews.map((n) => n.company.name))].map((nm) => (
                <label key={nm} className="gmm-checkbox">
                  <input
                    type="checkbox"
                    checked={!!advCos[nm]}
                    onChange={(e) => setAdvCos((p) => ({ ...p, [nm]: e.target.checked }))}
                  />{' '}
                  {nm}
                </label>
              ))}
            </div>
            <div className="gmm-checkbox-grid">
              <span className="gmm-strong">Source types</span>
              {filterCategories.sourcesContent.map((s) => (
                <label key={s.name} className="gmm-checkbox">
                  <input
                    type="checkbox"
                    checked={!!advSources[s.name]}
                    onChange={(e) =>
                      setAdvSources((p) => ({ ...p, [s.name]: e.target.checked }))
                    }
                  />{' '}
                  {s.name}
                </label>
              ))}
            </div>
            <div className="gmm-modal-actions">
              <button type="button" className="gmm-btn-muted" onClick={() => setAdvOpen(false)}>
                Cancel
              </button>
              <button type="button" className="gmm-btn-muted" onClick={() => advResetAll()}>
                Reset
              </button>
              <button
                type="button"
                className="gmm-btn-primary"
                onClick={() => {
                  setAdvOpen(false);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="newsfeed-sidebar">
        <div className="sidebar-section">
          <div className="time-filter-wrap">
            <button type="button" className="time-filter" onClick={() => setTimeOpen((v) => !v)}>
              {timeLabel}
              <span className="dropdown-arrow">▼</span>
            </button>
            {timeOpen ? (
              <div className="gmm-mini-panel">
                {['Last 7 days', 'Last 30 days', 'Last Quarter', 'YTD'].map((lbl) => (
                  <button
                    key={lbl}
                    type="button"
                    className="gmm-dropdown-item"
                    onClick={() => {
                      setTimeLabel(lbl);
                      setTimeOpen(false);
                    }}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {[
          ['COMPANIES', 'sidebar-heading--companies', filterCategories.companies, companiesSel, setCompaniesSel],
          ['TOPICS', 'sidebar-heading--topics', filterCategories.topics, topicsSel, setTopicsSel],
          [
            'SOURCES & CONTENT',
            'sidebar-heading--sources',
            filterCategories.sourcesContent,
            sourcesSel,
            setSourcesSel,
          ],
          ['LOCATIONS', 'sidebar-heading--locations', filterCategories.locations, locsSel, setLocsSel],
        ].map(([title, klass, lst, sel, setter]) => (
          <div key={title} className="sidebar-section">
            <h4 className={`sidebar-heading ${klass}`}>{title}</h4>
            {lst.map((filter) => {
              const active = sel.includes(filter.id);
              return (
                <button
                  key={filter.id}
                  type="button"
                  className={`filter-item ${active ? 'filter-item-active' : ''}`}
                  onClick={() => toggleSidebar(filter.id, sel, setter)}
                >
                  <span className="filter-icon filter-icon--initial">{companyInitial(filter.name)}</span>
                  <span className="filter-name">{filter.name}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="newsfeed-main">
        <div className="newsfeed-tabs">
          {['all', 'key', 'competitors', 'areas', 'tenders', 'leadership'].map((tid) => {
            const lbl =
              tid === 'all'
                ? 'All Updates'
                : tid === 'key'
                  ? 'Key Updates'
                  : tid === 'competitors'
                    ? 'Competitors'
                    : tid === 'areas'
                      ? 'Areas of Interest'
                      : tid === 'tenders'
                        ? 'Government Tenders'
                        : 'Leadership Speak';
            return (
              <button
                key={tid}
                type="button"
                className={`newsfeed-tab ${activeTab === tid ? 'active' : ''}`}
                onClick={() => setActiveTab(tid)}
              >
                {lbl}
              </button>
            );
          })}
          {customTabs.map((ct) => (
            <button
              key={ct.id}
              type="button"
              className={`newsfeed-tab ${activeTab === ct.id ? 'active' : ''}`}
              onClick={() => setActiveTab(ct.id)}
            >
              {ct.label}
            </button>
          ))}
          <div className="tab-add-host">
            <button type="button" className="newsfeed-tab-add" onClick={() => setTabAddOpen((v) => !v)}>
              +
            </button>
            {tabAddOpen ? (
              <div className="gmm-popover">
                <input
                  className="gmm-input"
                  placeholder="Tab name"
                  value={newTabLabel}
                  onChange={(e) => setNewTabLabel(e.target.value)}
                />
                <button
                  type="button"
                  className="gmm-btn-primary gmm-btn-small"
                  onClick={() => {
                    const lbl = newTabLabel.trim();
                    if (!lbl) return;
                    const id = `c-${Date.now()}`;
                    setCustomTabs((t) => [...t, { id, label: lbl }]);
                    setActiveTab(id);
                    setNewTabLabel('');
                    setTabAddOpen(false);
                  }}
                >
                  Add
                </button>
              </div>
            ) : null}
          </div>

          <div className="newsfeed-actions">
            <button
              type="button"
              className="action-icon-btn"
              onClick={() => clipboardWrite(window.location.href, 'Link copied')}
            >
              Copy View
            </button>
            <button type="button" className="action-primary-btn" onClick={() => setAddModal(true)}>
              Add Update
            </button>
            <button type="button" className="action-add-btn" onClick={() => setAddModal(true)}>
              +
            </button>
          </div>
        </div>

        {curationMode === 'Off' ? (
          <div className="newsfeed-banner-curation">Curation mode off</div>
        ) : null}

        <div className="newsfeed-search-bar">
          <div className="search-input-wrapper">
            <span className="search-icon search-icon--field" aria-hidden="true" />
            <input
              type="text"
              placeholder="Search"
              className="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button type="button" className="advanced-search-btn" onClick={() => setAdvOpen(true)}>
            Advanced Search
          </button>
          <div className="favourite-searches">
            <span>Favourite Searches</span>
            <div className="nf-fav-dd-wrap">
              <button
                type="button"
                className="filter-dropdown nf-fav-trigger"
                onClick={() => setFavDdOpen((o) => !o)}
              >
                Pick saved… ▾
              </button>
              {favDdOpen ? (
                <div className="gmm-mini-panel nf-fav-list">
                  {favouriteSearches.map((fs) => (
                    <button
                      key={fs.id}
                      type="button"
                      className="gmm-dropdown-item"
                      onClick={() => {
                        setSearch(fs.title);
                        setFavDdOpen(false);
                      }}
                    >
                      {fs.title}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="newsfeed-controls">
          <div className="control-group">
            <span className="control-label">Sort by:</span>
            <select className="control-dropdown" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
              <option>Date Published</option>
              <option>Relevance</option>
              <option>Company</option>
            </select>
          </div>
          <div className="control-group">
            <span className="control-label">Duplicates:</span>
            <select className="control-dropdown" value={dupLabel} onChange={(e) => setDupLabel(e.target.value)}>
              <option>Grouped</option>
              <option>Show All</option>
              <option>Collapse threads</option>
            </select>
          </div>
          <div className="control-group">
            <span className="control-label">User Comments:</span>
            <select
              className="control-dropdown"
              value={commentLabel}
              onChange={(e) => setCommentLabel(e.target.value)}
            >
              <option>All</option>
              <option>With Comments</option>
              <option>Unread Threads</option>
            </select>
          </div>
          <div className="control-group">
            <span className="control-label">Curation Mode:</span>
            <select className="control-dropdown" value={curationMode} onChange={(e) => setCurationMode(e.target.value)}>
              <option>On</option>
              <option>Off</option>
            </select>
          </div>
        </div>

        <div className="newsfeed-hud">
          <span>Duplicates mode: {dupLabel}</span>
          <span>User comments filter: {commentLabel}</span>
        </div>

        <div className="newsfeed-header">
          <div className="updates-count">
            <span className="count-icon" aria-hidden="true" />
            <span className="count-text">{filteredBase.length} Updates</span>
          </div>
          <div className="view-toggle">
            <button
              type="button"
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button
              type="button"
              className={`view-btn ${viewMode === 'visualize' ? 'active' : ''}`}
              onClick={() => setViewMode('visualize')}
            >
              Visualize
            </button>
          </div>
        </div>

        {viewMode === 'visualize' ? <NewsVizBars bars={vizBars} /> : null}

        {activeTab === 'leadership' && filteredBase.length === 0 ? (
          <div className="nf-empty-leadership">No leadership speak items matched these filters.</div>
        ) : null}

        {activeTab === 'tenders' && filteredBase.length === 0 ? (
          <div className="nf-empty-leadership">No government tenders matched these filters.</div>
        ) : null}

        {viewMode === 'list' &&
        !(
          (activeTab === 'leadership' || activeTab === 'tenders') &&
          filteredBase.length === 0
        ) ? (
          <div className="news-cards">
            {filteredBase.map((item) => {
              let topicsClass = '';
              if (topicsSel.length) topicsClass = 'nf-outline-topics';
              let locClass = '';
              if (locsSel.length) locClass = 'nf-outline-locations';
              const rc =
                companiesSel.length > 0
                  ? ribbonClass(companiesSel, filterCategories, item.company.name)
                  : '';
              return (
                <div
                  key={item.id}
                  className={`news-card ${rc} ${topicsClass} ${locClass} ${
                    item.reviewed ? 'news-card-reviewed' : ''
                  }`}
                >
                <div className="news-card-header">
                  <div className="company-info">
                    <div
                      className="company-logo-square company-logo-square--initial"
                      title={item.company.name}
                    >
                      <NewsCompanyAvatar company={item.company} />
                    </div>
                    <div className="company-details">
                      <div className="company-name-large">{item.company.name}</div>
                      <div className="news-meta">
                        {item.source} · {item.date} (Published on {item.publishedDate})
                      </div>
                    </div>
                  </div>
                  <div className="news-card-actions">
                    <button
                      type="button"
                      className="news-action-btn"
                      title="Add"
                      onClick={() => setWsModalCardId(item.id)}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className="news-action-btn"
                      title="Edit"
                      onClick={() =>
                        setItems((prev) =>
                          prev.map((x) =>
                            x.id === item.id
                              ? {
                                  ...x,
                                  editing: !x.editing,
                                  editedTitle: x.editedTitle ?? x.title,
                                  editedDesc: x.editedDesc ?? x.description,
                                }
                              : x,
                          ),
                        )
                      }
                    >
                      E
                    </button>
                    <button
                      type="button"
                      className="news-action-btn"
                      title="Mark as read"
                      onClick={() => {
                        setItems((prev) =>
                          prev.map((x) => (x.id === item.id ? { ...x, reviewed: true } : x)),
                        );
                        toast('Marked as reviewed');
                      }}
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      className={`news-action-btn ${item.bookmarked ? 'news-action-saved' : ''}`}
                      title="Save"
                      onClick={() => {
                        setItems((prev) =>
                          prev.map((x) =>
                            x.id === item.id ? { ...x, bookmarked: !x.bookmarked } : x,
                          ),
                        );
                        toast('Saved');
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="news-action-btn"
                      title="Share"
                      onClick={() => clipboardWrite(MOCK_INTEL_FEED_URL, 'Link copied')}
                    >
                      Share
                    </button>
                    <div className="nf-more-host">
                      <button
                        type="button"
                        className="news-action-btn"
                        title="More"
                        onClick={() => setMoreCardId((id) => (id === item.id ? null : item.id))}
                      >
                        ···
                      </button>
                      {moreCardId === item.id ? (
                        <div className="gmm-mini-panel nf-more-menu">
                          <button
                            type="button"
                            className="gmm-dropdown-item"
                            onClick={() => {
                              setItems((prev) =>
                                prev.map((x) => (x.id === item.id ? { ...x, hidden: true } : x)),
                              );
                              toast('Hidden from feed (demo)');
                              setMoreCardId(null);
                            }}
                          >
                            Hide
                          </button>
                          <button
                            type="button"
                            className="gmm-dropdown-item"
                            onClick={() => {
                              toast('Issue reported — ref NF-' + item.id);
                              setMoreCardId(null);
                            }}
                          >
                            Report issue
                          </button>
                          <button
                            type="button"
                            className="gmm-dropdown-item"
                            onClick={() => window.open(MOCK_INTEL_FEED_URL, '_blank')}
                          >
                            View source
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="news-card-body">
                  <div className="news-content">
                    {!item.editing ? (
                      <h3 className={`news-title ${item.reviewed ? 'news-title-struck' : ''}`}>
                        {item.editedTitle != null ? item.editedTitle : item.title}
                      </h3>
                    ) : (
                      <input
                        className="news-title-edit gmm-input"
                        value={item.editedTitle != null ? item.editedTitle : item.title}
                        onChange={(e) =>
                          setItems((prev) =>
                            prev.map((x) =>
                              x.id === item.id ? { ...x, editedTitle: e.target.value } : x,
                            ),
                          )
                        }
                      />
                    )}
                    {!item.editing ? (
                      <p className="news-description">
                        {item.editedDesc != null ? item.editedDesc : item.description}
                      </p>
                    ) : (
                      <textarea
                        className="news-desc-edit gmm-textarea"
                        value={item.editedDesc != null ? item.editedDesc : item.description}
                        rows={4}
                        onChange={(e) =>
                          setItems((prev) =>
                            prev.map((x) =>
                              x.id === item.id ? { ...x, editedDesc: e.target.value } : x,
                            ),
                          )
                        }
                      />
                    )}
                    {item.editing ? (
                      <div className="nf-edit-bar">
                        <button
                          type="button"
                          className="gmm-btn-primary gmm-btn-small"
                          onClick={() =>
                            setItems((prev) =>
                              prev.map((x) =>
                                x.id === item.id
                                  ? {
                                      ...x,
                                      editing: false,
                                      title:
                                        x.editedTitle != null && x.editedTitle.trim()
                                          ? x.editedTitle
                                          : x.title,
                                      description:
                                        x.editedDesc != null && String(x.editedDesc).trim()
                                          ? x.editedDesc
                                          : x.description,
                                      editedTitle: null,
                                      editedDesc: null,
                                    }
                                  : x,
                              ),
                            )
                          }
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="gmm-btn-muted gmm-btn-small"
                          onClick={() =>
                            setItems((prev) =>
                              prev.map((x) =>
                                x.id === item.id
                                  ? { ...x, editing: false, editedDesc: null, editedTitle: null }
                                  : x,
                              ),
                            )
                          }
                        >
                          Cancel
                        </button>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="read-more-btn"
                      onClick={() =>
                        setItems((prev) =>
                          prev.map((x) => (x.id === item.id ? { ...x, expanded: !x.expanded } : x)),
                        )
                      }
                    >
                      {item.expanded ? 'Read Less −' : 'Read More +'}
                    </button>
                    {item.expanded ? (
                      <p className="news-description nf-read-more-extra">{EXTRA_READ_MORE}</p>
                    ) : null}

                    {item.keyHighlights && item.keyHighlights.length > 0 ? (
                      <div className="key-highlights">
                        <div className="highlights-header">
                          <span className="highlights-title">Key Highlights</span>
                        </div>
                        <div className="highlights-list">
                          {item.keyHighlights.map((highlight) => (
                            <div key={highlight.id} className="highlight-item">
                              <input
                                type="checkbox"
                                checked={highlight.checked}
                                readOnly
                                className="highlight-checkbox"
                              />
                              <span className="highlight-text">{highlight.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="news-image">
                    <div className="news-thumbnail news-thumbnail--placeholder" aria-hidden />
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default NewsfeedPage;
